<script lang="ts">
import { computed, defineComponent, inject, ref, unref } from 'vue';

import {
  makeDisabledProps,
  makeFocusProps,
  makeInputProps,
  makeReadonlyProps,
  makeValidationProps,
  useFilteredInputAttrs,
  useFilteredInputProps,
  useFocus,
  useInput
} from '../../composable';
import { CdsInput, CdsInputDescription, CdsInputLabel } from '../CdsInput';

import { RadioButtonKey, radioButtonProvideDefaultValue } from './CdsRadioButton.vue';

import type { IRadioButtonProvide } from './CdsRadioButton.vue';

export const componentName = 'CdsRadio';
export default defineComponent({
  name: componentName,
  components: { CdsInput, CdsInputLabel, CdsInputDescription },
  props: {
    ...makeInputProps(),
    ...makeValidationProps({
      hideMessages: true
    }),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true
  },
  setup(props, { attrs, slots, expose }) {
    const { id, hasLabel, hasDescription } = useInput(props, slots, 'radio');
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);
    const { inputProps } = useFilteredInputProps(props, ['hideMessages']);

    const validationRef = ref<typeof CdsInput>();

    const { isGroup, model, disabledGroup, readonlyGroup, errorGroup, handleChangeRadio } = inject<IRadioButtonProvide>(
      RadioButtonKey,
      radioButtonProvideDefaultValue
    );

    const internalDisabled = computed(() => unref(disabledGroup) || props.disabled);
    const internalReadonly = computed(() => unref(readonlyGroup) || props.readonly);
    const internalError = computed(() => unref(errorGroup) || props.error);
    const internalHideMessages = computed(() => unref(isGroup) || props.hideMessages);

    const checked = computed(() => unref(model) === props.value);

    const radioClasses = computed(() => ({
      'cds-radio--disabled': unref(internalDisabled),
      'cds-radio--checked': unref(model)
    }));

    const onChange = (event: Event) => {
      const value = (event.target as HTMLInputElement).value;
      handleChangeRadio(value);
    };

    expose({
      id,
      validationRef,
      isGroup
    });

    return {
      id,
      model,
      checked,
      hasLabel,
      hasDescription,
      validationRef,
      isGroup,
      isFocused,
      inputProps,
      rootAttrs,
      inputAttrs,
      internalDisabled,
      internalReadonly,
      internalError,
      internalHideMessages,
      radioClasses,
      onBlur: blur,
      onFocus: focus,
      onChange
    };
  }
});
</script>

<template>
  <cds-input
    :id="id"
    ref="validationRef"
    :focused="isFocused"
    class="cds-radio"
    :class="radioClasses"
    v-bind="{ ...inputProps, ...rootAttrs }"
    :hide-messages="internalHideMessages"
  >
    <template #default="{ isDisabled, isReadonly, isValid }">
      <input
        :id="id"
        type="radio"
        :name="name"
        :checked="checked"
        :disabled="internalDisabled || internalReadonly || isDisabled.value || isReadonly.value"
        :aria-disabled="internalDisabled || internalReadonly || isDisabled.value || isReadonly.value"
        :aria-checked="checked"
        :aria-invalid="internalError || isValid.value === false"
        :value="value"
        v-bind="inputAttrs"
        @blur="onBlur"
        @focus="onFocus"
        @change="onChange"
      />
      <cds-input-label v-if="hasLabel" :for="id" :text="label">
        <!--
        @slot Лейбл
        @type string | html
      -->
        <slot name="label" />
      </cds-input-label>
      <cds-input-description v-if="hasDescription" :text="description">
        <!--
        @slot Описание
        @type string | html
      -->
        <slot name="description" />
      </cds-input-description>
    </template>
    <template #messages>
      <!--
        @slot Сообщения и подсказки
        @type string | html
      -->
      <slot name="messages" />
    </template>
  </cds-input>
</template>
